/**
 * Browser environment check utility
 * Safely checks for browser APIs without triggering Node.js polyfills
 */

/**
 * Check if we're in Node.js build environment (webpack/html-webpack-plugin)
 * This detects Node.js environments where accessing localStorage triggers webstorage error
 * @returns {boolean} True if in Node.js build environment
 */
const isNodeBuildEnvironment = () => {
  try {
    // Primary check: Are we in Node.js?
    const isNode = typeof process !== 'undefined' && 
                   process.versions && 
                   process.versions.node;
    
    if (!isNode) {
      return false; // Not Node.js, assume browser
    }
    
    // We're in Node.js - check if it's a build environment
    // In webpack/html-webpack-plugin, window might exist but localStorage will error
    if (typeof window !== 'undefined') {
      // Check if we have browser-like APIs that indicate real browser
      // If document doesn't exist or doesn't have typical browser methods, we're in build
      if (typeof document === 'undefined' || 
          typeof document.createElement !== 'function' ||
          typeof document.body === 'undefined') {
        return true; // Node.js build environment
      }
      
      // Check for webpack/build indicators
      if (typeof __webpack_require__ !== 'undefined' || 
          (typeof module !== 'undefined' && module.hot) ||
          typeof navigator === 'undefined') {
        return true; // Likely build environment
      }
    }
    
    return true; // Node.js environment (default to safe)
  } catch (e) {
    // On any error, assume we're in Node.js to be safe
    return true;
  }
};

/**
 * Check if localStorage is available without triggering Node.js webstorage
 * Never accesses localStorage directly to avoid triggering the getter
 * @returns {boolean} True if localStorage is available
 */
export const hasLocalStorage = () => {
  // If we're in Node.js build environment, localStorage is not available
  if (isNodeBuildEnvironment()) {
    return false;
  }
  
  // Check if we're in a browser
  if (typeof window === 'undefined') {
    return false;
  }
  
  // Use 'in' operator to check property existence without triggering getter
  try {
    return 'localStorage' in window;
  } catch (e) {
    return false;
  }
};

/**
 * Get localStorage safely, returns null if not available
 * Uses a cached reference to avoid repeated property access
 * @returns {Storage|null} localStorage or null
 */
let _localStorageCache = null;
let _localStorageChecked = false;

export const getLocalStorage = () => {
  // Return cached value if already checked
  if (_localStorageChecked) {
    return _localStorageCache;
  }
  
  _localStorageChecked = true;
  
  // Double-check we're not in Node.js build
  if (isNodeBuildEnvironment()) {
    _localStorageCache = null;
    return null;
  }
  
  if (!hasLocalStorage()) {
    _localStorageCache = null;
    return null;
  }
  
  try {
    // Only access localStorage if we've passed all checks
    // This should only execute in true browser environments
    // Cache it to avoid repeated property access
    _localStorageCache = window.localStorage;
    return _localStorageCache;
  } catch (e) {
    // Catch any errors (including SecurityError from Node.js webstorage)
    _localStorageCache = null;
    return null;
  }
};

